import { createContext, useContext, useMemo, ReactNode } from 'react'
import { getBrowserTimezone } from '@/components/SemanticDatePicker/utils/timezone'

/**
 * Default timezone for new organizations.
 * US Eastern (Ohio) aligns with AWS us-east-2 server location.
 */
export const DEFAULT_TIMEZONE = 'America/New_York'

interface TimezoneContextValue {
  /**
   * The effective timezone for the current organization.
   * Falls back to browser timezone if org timezone is not set.
   */
  timezone: string

  /**
   * Whether the timezone is from the organization (true) or browser fallback (false).
   */
  isOrgTimezone: boolean
}

const TimezoneContext = createContext<TimezoneContextValue | undefined>(undefined)

interface TimezoneProviderProps {
  /**
   * Organization's configured timezone (IANA format).
   * Pass from currentOrganization.timezone.
   * Falls back to browser timezone if undefined/null.
   */
  orgTimezone?: string | null
  children: ReactNode
}

/**
 * Provides organization timezone throughout the app.
 *
 * Usage:
 * ```tsx
 * <TimezoneProvider orgTimezone={user.currentOrganization?.timezone}>
 *   <App />
 * </TimezoneProvider>
 * ```
 *
 * The timezone is used by SemanticDatePicker for:
 * - Displaying timezone abbreviations (e.g., "EST", "EDT")
 * - Calculating preset values (tomorrow, next month, etc.)
 */
export function TimezoneProvider({ orgTimezone, children }: TimezoneProviderProps) {
  const value = useMemo<TimezoneContextValue>(() => {
    const browserTimezone = getBrowserTimezone()

    // Use org timezone if set, otherwise fall back to browser
    if (orgTimezone) {
      return {
        timezone: orgTimezone,
        isOrgTimezone: true,
      }
    }

    return {
      timezone: browserTimezone,
      isOrgTimezone: false,
    }
  }, [orgTimezone])

  return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>
}

/**
 * Hook to access the current timezone.
 *
 * Usage:
 * ```tsx
 * function MyComponent() {
 *   const { timezone, isOrgTimezone } = useTimezone()
 *   // timezone is always a valid IANA string
 * }
 * ```
 *
 * If used outside TimezoneProvider, falls back to browser timezone
 * with a console warning in development.
 */
export function useTimezone(): TimezoneContextValue {
  const context = useContext(TimezoneContext)

  if (!context) {
    // Graceful fallback - allows components to work outside provider during dev
    if (import.meta.env.DEV) {
      console.warn(
        'useTimezone() called outside TimezoneProvider. ' +
          'Falling back to browser timezone. ' +
          'Wrap your app in <TimezoneProvider> for org timezone support.'
      )
    }

    return {
      timezone: getBrowserTimezone(),
      isOrgTimezone: false,
    }
  }

  return context
}
